import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { sendEmailVerification } from 'firebase/auth';
import { useAuth } from '@/contexts/AuthContext';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { MicrosoftIcon } from '@/components/icons/MicrosoftIcon';
import { AuthThemeToggle } from '@/components/auth/AuthThemeToggle';
import { AuthLanguageSwitcher } from '@/components/auth/AuthLanguageSwitcher';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { logger } from '@/lib/logger';
import { atomicRegistration, inviteOnlyRegistration } from '@/services/atomicRegistration.service';
import { accountOnlyRegistration } from '@/services/accountOnlyRegistration.service';
import { getEmailVerificationActionCodeSettings } from '@/lib/auth/emailVerificationConfig';
import { auth } from '@/lib/firebase';
import {
  combinedSignupSchema,
  inviteSignupSchema,
  REFERRAL_SOURCE_OPTIONS,
  type CombinedSignupFormValues,
  type InviteSignupFormValues,
} from './combinedSignup.schema';
import { cn } from '@/lib/utils';
import { getPendingInvite } from '@/app/routes/invite/AcceptInvitePage';
import { isFeatureEnabled } from '@/config/featureFlags';
import { getUserActiveMemberships } from '@/services/membershipIndex.service';
import { authToast } from '@/lib/toast/authToast';
import { SignupHeroSection, SignupMediaPanel, SignupFormCard } from './components';
import { useSignupReveal } from './hooks/useSignupReveal';

export default function SignUpPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, signInWithGoogle, signInWithMicrosoft } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Check if this is an invite signup flow
  const inviteId = searchParams.get('inviteId');
  const inviteTeamId = searchParams.get('team');
  const inviteEmail = searchParams.get('email') || '';

  // Also check sessionStorage for invite info (fallback)
  const pendingInvite = getPendingInvite();
  const effectiveInviteId = inviteId || pendingInvite?.inviteId;
  const effectiveTeamId = inviteTeamId || pendingInvite?.teamId;
  const effectiveEmail = inviteEmail || pendingInvite?.email || '';
  const effectiveIsInviteFlow = Boolean(effectiveInviteId && effectiveTeamId);

  // Use different schema for invite flow (no company fields required)
  const form = useForm<CombinedSignupFormValues | InviteSignupFormValues>({
    resolver: zodResolver(effectiveIsInviteFlow ? inviteSignupSchema : combinedSignupSchema),
    defaultValues: {
      email: effectiveEmail,
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      referralSource: effectiveIsInviteFlow ? 'added_to_team' : undefined,
      referralOther: '',
    },
    mode: 'onChange',
  });

  // Watch referralSource to conditionally show "Other" field
  const referralSource = form.watch('referralSource');

  // Email is now read-only for invite flow, no need to track changes

  const { formState, setFocus } = form;
  const { errors, isValid } = formState;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // If in invite flow, redirect back to invite page
      if (effectiveIsInviteFlow && effectiveInviteId && effectiveTeamId) {
        navigate(`/invite/${effectiveInviteId}?team=${effectiveTeamId}`, { replace: true });
      } else {
        // Already authenticated - let OnboardingGuard handle the routing
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, effectiveIsInviteFlow, effectiveInviteId, effectiveTeamId]);

  // Focus first invalid field after failed submit
  useEffect(() => {
    if (hasSubmitError && Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0] as keyof CombinedSignupFormValues;
      setFocus(firstErrorField);
      setHasSubmitError(false);
    }
  }, [hasSubmitError, errors, setFocus]);

  const onSubmit = async (data: CombinedSignupFormValues | InviteSignupFormValues) => {
    try {
      setIsSubmitting(true);
      setSignupError(null);

      let result;

      // Derive fullName from firstName + lastName
      const fullName = `${data.firstName} ${data.lastName}`.trim();

      if (effectiveIsInviteFlow) {
        // Invite flow - create user only, no team
        logger.info('[SignUpPage] Invite signup flow:', {
          inviteId: effectiveInviteId,
          teamId: effectiveTeamId,
        });

        result = await inviteOnlyRegistration({
          firstName: data.firstName,
          lastName: data.lastName,
          fullName,
          email: data.email,
          password: data.password,
          inviteId: effectiveInviteId!,
          teamId: effectiveTeamId!,
          referralSource: data.referralSource,
          referralOther: data.referralOther,
        });

        if (result.success) {
          setShowSuccess(true);
          toast.success(t('auth.signUp.success'));
          logger.info('[SignUpPage] Invite registration successful:', { userId: result.userId });

          // Redirect to invite page to accept the invite
          setTimeout(() => {
            navigate(`/invite/${effectiveInviteId}?team=${effectiveTeamId}`, { replace: true });
          }, 800);
        }
      } else {
        // Normal flow - create account ONLY (no team)
        // Contract §1: R_SIGNUP (account) → R_NEW_TEAM → R_VERIFY_NOTICE
        // Team creation happens at /onboarding/create-team for ALL new users
        const fullData = data as CombinedSignupFormValues;
        result = await accountOnlyRegistration({
          firstName: fullData.firstName,
          lastName: fullData.lastName,
          fullName,
          email: fullData.email,
          password: fullData.password,
          referralSource: fullData.referralSource,
          referralOther: fullData.referralOther,
        });

        if (result.success) {
          setShowSuccess(true);
          toast.success(t('auth.signUp.success'), {
            description: t('auth.signUp.accountCreated', 'Account created successfully'),
          });
          logger.info('[SignUpPage] Account-only registration successful:', {
            userId: result.userId,
          });

          // CONTRACT v2.0.0: Send verification email immediately after signup
          // Verification MUST happen BEFORE team creation
          try {
            if (auth.currentUser && !auth.currentUser.emailVerified) {
              logger.info('[SignUpPage] Sending verification email...');
              await sendEmailVerification(
                auth.currentUser,
                getEmailVerificationActionCodeSettings()
              );
              logger.info('[SignUpPage] Verification email sent successfully');
              toast.info(t('auth.signUp.verificationEmailSent', 'Verification email sent'), {
                description: t(
                  'auth.signUp.checkInbox',
                  'Check your inbox to verify your email address'
                ),
              });
            }
          } catch (emailError) {
            logger.error('[SignUpPage] Failed to send verification email:', emailError);
            // Non-blocking - user can resend from /verify-email page
            toast.warning(
              t('auth.signUp.verificationEmailFailed', 'Could not send verification email'),
              {
                description: t(
                  'auth.signUp.canResend',
                  'You can resend it from the verification page'
                ),
              }
            );
          }

          // Navigate after email send attempt (success or failure)
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 500);
        }
      }

      if (result && !result.success) {
        // Show error from registration - translate the error key
        const errorKey = result.error || 'auth.signUp.errors.generic';
        setSignupError(t(errorKey));
        logger.error('[SignUpPage] Registration failed:', result.errorCode);
      }
    } catch (error) {
      // Unexpected error
      setSignupError(t('auth.signUp.errors.generic'));
      logger.error('[SignUpPage] Unexpected error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvalidSubmit = () => {
    setHasSubmitError(true);
  };

  // Google Sign-Up handler
  // SIGNUP PAGE RULE: If user already has memberships, auto-login them instead of creating new account
  const handleGoogleSignUp = async () => {
    // Don't allow OAuth signup in invite flow - they need to use email/password
    if (effectiveIsInviteFlow) {
      setSignupError(
        t(
          'auth.signUp.errors.oauthNotAllowedForInvite',
          'Gebruik e-mail en wachtwoord voor uitnodigingsregistratie.'
        )
      );
      return;
    }

    try {
      setIsGoogleLoading(true);
      setSignupError(null);

      const result = await signInWithGoogle();

      // CHECK: Does this user already have memberships (existing Modcar account)?
      const memberships = await getUserActiveMemberships(result.user.uid);

      if (memberships.length > 0) {
        // Existing user with memberships - auto-login them
        logger.info('[SignUpPage] Existing Google user with memberships, auto-logging in');
        setShowSuccess(true);
        authToast.loginSuccess();
        toast.info(
          t('auth.signIn.existingAccountToast', 'Je hebt al een account. We hebben je ingelogd.')
        );
      } else {
        // New user - show success
        setShowSuccess(true);
        toast.success(t('auth.signUp.success'));
        logger.info('[SignUpPage] Google signup successful for new user');
      }

      // CLEANUP: Always navigate to neutral entry (/dashboard)
      // Let guards + resolver decide final destination (create-team / onboarding / dashboard)
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 500);
    } catch (error) {
      let errorMessage = t('auth.signUp.errors.generic');

      if (error && typeof error === 'object' && 'code' in error) {
        const code = error.code as string;
        if (code === 'auth/account-exists-with-different-credential') {
          errorMessage = t('auth.signIn.errors.accountExistsWithDifferentCredential');
        } else if (
          code === 'auth/popup-closed-by-user' ||
          code === 'auth/cancelled-popup-request'
        ) {
          setIsGoogleLoading(false);
          return;
        } else if (code === 'auth/network-request-failed') {
          errorMessage = t('auth.signIn.errors.networkError');
        }
      }

      if (error instanceof Error && error.message === 'REDIRECT_IN_PROGRESS') {
        return;
      }

      setSignupError(errorMessage);
      logger.error('[SignUpPage] Google signup error:', error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Microsoft Sign-Up handler
  // SIGNUP PAGE RULE: If user already has memberships, auto-login them instead of creating new account
  const handleMicrosoftSignUp = async () => {
    // Don't allow OAuth signup in invite flow - they need to use email/password
    if (effectiveIsInviteFlow) {
      setSignupError(
        t(
          'auth.signUp.errors.oauthNotAllowedForInvite',
          'Gebruik e-mail en wachtwoord voor uitnodigingsregistratie.'
        )
      );
      return;
    }

    try {
      setIsMicrosoftLoading(true);
      setSignupError(null);

      const result = await signInWithMicrosoft();

      // CHECK: Does this user have any active memberships (i.e., a Modcar account)?
      const memberships = await getUserActiveMemberships(result.user.uid);

      if (memberships.length > 0) {
        // Existing user with memberships - auto-login them
        logger.info('[SignUpPage] Existing Microsoft user with memberships, auto-logging in');
        setShowSuccess(true);
        authToast.loginSuccess();
        toast.info(
          t('auth.signIn.existingAccountToast', 'Je hebt al een account. We hebben je ingelogd.')
        );
      } else {
        // New user - show success
        setShowSuccess(true);
        toast.success(t('auth.signUp.success'));
        logger.info('[SignUpPage] Microsoft signup successful for new user');
      }

      // CLEANUP: Always navigate to neutral entry (/dashboard)
      // Let guards + resolver decide final destination (create-team / onboarding / dashboard)
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 500);
    } catch (error) {
      let errorMessage = t('auth.signUp.errors.generic');

      if (error && typeof error === 'object' && 'code' in error) {
        const code = error.code as string;
        if (code === 'auth/account-exists-with-different-credential') {
          errorMessage = t('auth.signIn.errors.accountExistsWithDifferentCredential');
        } else if (
          code === 'auth/popup-closed-by-user' ||
          code === 'auth/cancelled-popup-request'
        ) {
          setIsMicrosoftLoading(false);
          return;
        } else if (code === 'auth/network-request-failed') {
          errorMessage = t('auth.signIn.errors.networkError');
        }
      }

      if (error instanceof Error && error.message === 'REDIRECT_IN_PROGRESS') {
        return;
      }

      setSignupError(errorMessage);
      logger.error('[SignUpPage] Microsoft signup error:', error);
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  const isButtonDisabled = !isValid || isSubmitting || isGoogleLoading || isMicrosoftLoading;

  // Choreography: cinematic reveal - hero first, then form
  const { showHero, showForm, reducedMotion } = useSignupReveal();

  return (
    <div className="min-h-screen w-full bg-background">
      {/* Top-right controls */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <AuthThemeToggle />
        <AuthLanguageSwitcher />
      </div>

      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left side: Normal document flow - hero then form, no overlap possible */}
        <div className="flex-1 px-6 lg:px-12 xl:px-20 py-8 lg:py-12 overflow-y-auto">
          <div className="w-full max-w-[480px] mx-auto flex flex-col">
            {/* HERO SECTION: In normal flow, always above form */}
            <div
              className={cn(
                'transition-opacity duration-500 ease-out',
                reducedMotion && 'transition-none',
                showHero ? 'opacity-100' : 'opacity-0'
              )}
            >
              <SignupHeroSection showHero={showHero} reducedMotion={reducedMotion} />
            </div>

            {/* Spacing between hero and form */}
            <div className="h-6 lg:h-8" aria-hidden="true" />

            {/* Form card - revealed after hero with smooth transition */}
            <SignupFormCard showForm={showForm} reducedMotion={reducedMotion}>
              <Card
                className={cn(
                  'rounded-2xl shadow-lg border-border transition-transform mt-5',
                  'motion-reduce:transition-none',
                  hasSubmitError && 'animate-shake',
                  showSuccess && 'opacity-95 scale-[0.98]'
                )}
              >
                <CardContent className="pt-4 pb-3 px-5">
                  <Form {...form}>
                    <form
                      action="#"
                      onSubmit={(e) => void form.handleSubmit(onSubmit, handleInvalidSubmit)(e)}
                      className="space-y-2.5"
                      aria-busy={isSubmitting}
                      noValidate
                    >
                      {/* === 1. EMAIL FIELD === */}
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="text-xs font-medium">
                              {t('auth.signUp.email')}
                              {effectiveIsInviteFlow && (
                                <span className="ml-1 text-muted-foreground font-normal">
                                  ({t('auth.signUp.inviteEmailLocked')})
                                </span>
                              )}
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder={t('auth.signUp.emailPlaceholder')}
                                autoComplete="email"
                                autoFocus
                                disabled={isSubmitting || showSuccess || effectiveIsInviteFlow}
                                readOnly={effectiveIsInviteFlow}
                                className={cn('h-9', effectiveIsInviteFlow && 'bg-muted')}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" aria-live="polite" />
                          </FormItem>
                        )}
                      />

                      {/* === 2. PASSWORD + CONFIRM === */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <FormField
                          control={form.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem className="space-y-1">
                              <FormLabel className="text-xs font-medium">
                                {t('auth.signUp.password')}
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder={t('auth.signUp.passwordPlaceholder')}
                                    autoComplete="new-password"
                                    disabled={isSubmitting || showSuccess}
                                    className="h-9 pr-9"
                                    {...field}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isSubmitting || showSuccess}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                                    aria-label={
                                      showPassword
                                        ? t('auth.signIn.hidePassword')
                                        : t('auth.signIn.showPassword')
                                    }
                                  >
                                    {showPassword ? (
                                      <EyeOff className="h-3.5 w-3.5" />
                                    ) : (
                                      <Eye className="h-3.5 w-3.5" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-xs" aria-live="polite" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem className="space-y-1">
                              <FormLabel className="text-xs font-medium">
                                {t('auth.signUp.confirmPassword')}
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder={t('auth.signUp.confirmPasswordPlaceholder')}
                                    autoComplete="new-password"
                                    disabled={isSubmitting || showSuccess}
                                    className="h-9 pr-9"
                                    {...field}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    disabled={isSubmitting || showSuccess}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                                    aria-label={
                                      showConfirmPassword
                                        ? t('auth.signIn.hidePassword')
                                        : t('auth.signIn.showPassword')
                                    }
                                  >
                                    {showConfirmPassword ? (
                                      <EyeOff className="h-3.5 w-3.5" />
                                    ) : (
                                      <Eye className="h-3.5 w-3.5" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-xs" aria-live="polite" />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* === 3. FIRST NAME + LAST NAME === */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <FormField
                          control={form.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem className="space-y-1">
                              <FormLabel className="text-xs font-medium">
                                {t('auth.signUp.firstName', 'Voornaam')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="text"
                                  placeholder={t('auth.signUp.firstNamePlaceholder', 'Jan')}
                                  autoComplete="given-name"
                                  disabled={isSubmitting || showSuccess}
                                  className="h-9"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-xs" aria-live="polite" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem className="space-y-1">
                              <FormLabel className="text-xs font-medium">
                                {t('auth.signUp.lastName', 'Achternaam')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="text"
                                  placeholder={t('auth.signUp.lastNamePlaceholder', 'Jansen')}
                                  autoComplete="family-name"
                                  disabled={isSubmitting || showSuccess}
                                  className="h-9"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-xs" aria-live="polite" />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* === 5. REFERRAL SOURCE DROPDOWN (de-emphasized) === */}
                      <div className="pt-1">
                        <FormField
                          control={form.control}
                          name="referralSource"
                          render={({ field }) => (
                            <FormItem className="space-y-1">
                              <FormLabel className="text-[11px] font-normal text-muted-foreground">
                                {t('auth.signUp.referralSource.label')}
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={isSubmitting || showSuccess || effectiveIsInviteFlow}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue
                                      placeholder={t('auth.signUp.referralSource.placeholder')}
                                    />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {REFERRAL_SOURCE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {t(`auth.signUp.referralSource.options.${option.value}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-xs" aria-live="polite" />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* === 6. CONDITIONAL "OTHER" FIELD === */}
                      {referralSource === 'other' && (
                        <FormField
                          control={form.control}
                          name="referralOther"
                          render={({ field }) => (
                            <FormItem className="space-y-1">
                              <FormLabel className="text-xs font-medium">
                                {t('auth.signUp.referralSource.otherLabel')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="text"
                                  placeholder={t('auth.signUp.referralSource.otherPlaceholder')}
                                  disabled={isSubmitting || showSuccess}
                                  className="h-9"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-xs" aria-live="polite" />
                            </FormItem>
                          )}
                        />
                      )}

                      {/* Signup Error Message */}
                      {signupError && (
                        <div
                          className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2 mt-2"
                          role="alert"
                          aria-live="assertive"
                        >
                          {signupError}
                        </div>
                      )}

                      {/* Create Account Button */}
                      <Button
                        type="submit"
                        disabled={isButtonDisabled || showSuccess}
                        className={cn(
                          'w-full h-11 rounded-xl font-semibold transition-all duration-200 mt-4',
                          isButtonDisabled || showSuccess
                            ? 'bg-muted text-muted-foreground'
                            : 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-lg shadow-[#7C3AED]/30',
                          showSuccess && 'bg-green-500 hover:bg-green-500 text-white'
                        )}
                      >
                        {showSuccess ? (
                          <>
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            {t('auth.signUp.success')}
                          </>
                        ) : isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('auth.signUp.submitting')}
                          </>
                        ) : (
                          t('auth.signUp.submit')
                        )}
                      </Button>

                      {/* OAuth Signup Buttons - Only show for non-invite flow */}
                      {!effectiveIsInviteFlow && (
                        <>
                          {/* Divider */}
                          <div className="relative my-4">
                            <div className="absolute inset-0 flex items-center">
                              <Separator className="w-full" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-card px-2 text-muted-foreground">
                                {t('auth.signIn.orContinueWith', 'of')}
                              </span>
                            </div>
                          </div>

                          {/* Google Sign-Up Button */}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleGoogleSignUp}
                            disabled={
                              isGoogleLoading || isMicrosoftLoading || isSubmitting || showSuccess
                            }
                            className="w-full h-10 rounded-xl font-medium text-sm"
                          >
                            {isGoogleLoading ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <GoogleIcon className="mr-2 h-5 w-5" />
                            )}
                            {t('auth.signIn.continueWithGoogle')}
                          </Button>

                          {/* Microsoft Sign-Up Button */}
                          {isFeatureEnabled('microsoftLogin') && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleMicrosoftSignUp}
                              disabled={
                                isMicrosoftLoading || isGoogleLoading || isSubmitting || showSuccess
                              }
                              className="w-full h-10 rounded-xl font-medium text-sm mt-2"
                            >
                              {isMicrosoftLoading ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <MicrosoftIcon className="mr-2 h-5 w-5" />
                              )}
                              {t('auth.signIn.continueWithMicrosoft')}
                            </Button>
                          )}
                        </>
                      )}
                    </form>
                  </Form>
                </CardContent>

                <CardFooter className="flex flex-col space-y-2 px-6 pb-4 pt-0">
                  <Separator />
                  <div className="text-center text-xs text-muted-foreground">
                    {t('auth.signUp.hasAccount')}{' '}
                    <Link
                      to="/login"
                      className="font-medium hover:underline"
                      style={{ color: '#CDB4F8' }}
                      tabIndex={isSubmitting ? -1 : 0}
                    >
                      {t('auth.signUp.signIn')}
                    </Link>
                  </div>
                </CardFooter>
              </Card>
            </SignupFormCard>
          </div>
        </div>

        {/* Right side: Media panel with floating KPI cards */}
        <div className="hidden lg:flex flex-1 relative">
          {/* Soft seam fade: gradient overlay at left edge */}
          <div
            className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none"
            aria-hidden="true"
          />
          <SignupMediaPanel />
        </div>

        {/* Mobile: Simplified media panel below form */}
        <div className="lg:hidden px-6 pb-12">
          <SignupMediaPanel />
        </div>
      </div>
    </div>
  );
}
